(() => {
  'use strict';
  if (window.UCWSearch) return;
  const cache = new Map(), indexes = new Map();
  const brands = new Set(['hp','brother','canon','epson','samsung','oki','xerox','kyocera','lexmark','dell','ricoh','huismerk']);
  const normalize = value => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const compact = value => normalize(value).replace(/[^a-z0-9]/g, '');
  function variants(value) {
    const spaced = normalize(value).replace(/([a-z])(\d)/g, '$1 $2').replace(/(\d)([a-z])/g, '$1 $2').replace(/[^a-z0-9]+/g, ' ').trim().replace(/\b(office|laser|desk) jet\b/g, '$1jet').replace(/\b(hl|mfc|dcp)([lj]) (?=\d)/g, '$1 $2 ');
    if (!spaced) return [];
    const tokens = spaced.split(' '), joined = [], hyphenated = [];
    for (let i = 0; i < tokens.length; i++) {
      let code = tokens[i], prefix = '';
      if (!brands.has(code) && /^[a-z]+$/.test(code) && /^\d/.test(tokens[i + 1] || '')) { prefix = code; code += tokens[++i]; }
      if (/\d$/.test(code) && /^[a-z]{1,3}$/.test(tokens[i + 1] || '') && !brands.has(tokens[i + 1])) code += tokens[++i];
      joined.push(code); hyphenated.push(prefix ? code.replace(prefix, prefix + '-') : code);
    }
    const result = [spaced, joined.join(' '), hyphenated.join(' '), joined.join(' ').replace(/\b(hl|mfc|dcp) ([lj]\d)/g, '$1$2')];
    if (/^\d{3,4}$/.test(spaced)) result.push('t' + spaced);
    return [...new Set(result)];
  }
  function queryWords(query) {
    // Formatting variants only; never correct numeric model numbers.
    if (/^\d+$/.test(normalize(query).trim())) return [normalize(query).trim()];
    const forms = variants(query);
    return (forms[1] || forms[0] || '').split(' ').filter(Boolean).map(compact);
  }
  function matches(field, word, prefix = false) {
    const text = compact(field);
    if (!/\d/.test(word)) return text.includes(word);
    const pattern = word.replace(/\d+/g, (number, offset) => '(?<![0-9])' + number + (prefix && offset + number.length === word.length ? '' : '(?![0-9])'));
    const expression = new RegExp(pattern + (/[a-z]$/.test(word) ? '(?![a-z])' : ''));
    return queryWords(field).some(token => expression.test(token));
  }
  function score(item, query, prefix = false) {
    const words = queryWords(query);
    const fields = [item.title, ...(Array.isArray(item.keywords) ? item.keywords : String(item.keywords || '').split(/[,;\n]/))];
    if (!words.length || !words.every((word, index) => fields.some(field => matches(field, word, prefix && index === words.length - 1 && /\d{2,}$/.test(word))))) return 0;
    if (prefix) return 500;
    const title = compact(item.title), needle = compact(query);
    if (title === needle) return 1000;
    if (fields.slice(1).some(field => compact(field) === needle)) return 950;
    if (title.endsWith(needle)) return 900;
    if (title.includes(needle)) return 800;
    return words.every(word => matches(item.title, word)) ? 700 : 600;
  }
  function merge(groups, query) {
    const unique = new Map();
    groups.flat().forEach(item => { if (!unique.has(item.key)) unique.set(item.key, item); });
    const all = [...unique.values()], exact = all.filter(item => score(item, query));
    // A completed match wins; only unfinished input falls back to a numeric prefix.
    const items = exact.length ? exact : all.filter(item => score(item, query, true));
    return items.sort((a, b) => score(b, query) - score(a, query) || Number(a.unavailable || false) - Number(b.unavailable || false) || a.title.localeCompare(b.title, 'nl', { numeric: true, sensitivity: 'base' }));
  }
  function readItems(root) {
    return [...root.querySelectorAll('[data-search-item]')].map(el => ({ key: el.dataset.searchKey, kind: el.dataset.searchKind, title: el.dataset.searchTitle || '', handle: el.dataset.searchHandle, html: el.outerHTML })).filter(item => item.key);
  }
  function abortCheck(signal) { if (signal?.aborted) throw new DOMException('Aborted', 'AbortError'); }
  async function request(url, signal) {
    abortCheck(signal);
    const key = url.href, saved = cache.get(key);
    if (saved && saved.expires > Date.now()) return saved.text;
    const timeout = AbortSignal.timeout(15000), combined = signal ? AbortSignal.any([signal, timeout]) : timeout;
    const response = await fetch(key, { credentials: 'same-origin', signal: combined });
    if (!response.ok) throw new Error('search_unavailable');
    const text = await response.text(); abortCheck(signal);
    cache.delete(key); cache.set(key, { text, expires: Date.now() + 60000 });
    while (cache.size > 40) cache.delete(cache.keys().next().value);
    return text;
  }
  function collectionBase() {
    return document.querySelector('[data-ucw-search]')?.dataset.collectionsUrl || '/collections';
  }
  async function index(kind) {
    const path = collectionBase() + (kind === 'product' ? '/all' : '');
    const key = 'ucw-search-v3:' + location.host + ':' + document.documentElement.lang + ':' + path;
    const saved = indexes.get(key);
    if (saved?.expires > Date.now()) return saved.items;
    if (saved?.pending) return saved.pending;
    try {
      const stored = JSON.parse(sessionStorage.getItem(key));
      if (stored?.expires > Date.now() && Array.isArray(stored.items)) { indexes.set(key, stored); return stored.items; }
    } catch (_) { /* Optional public-data cache. */ }
    // Input changes share a complete load rather than restart all requests.
    const pending = (async () => {
      async function batch(page) {
        const target = new URL(path, location.origin);
        target.search = new URLSearchParams({ view: 'ucw-search-index', page: String(page) });
        const data = JSON.parse(await request(target));
        if (data.version !== 1 || data.page !== page || !Array.isArray(data.items) || !Number.isInteger(data.pages) || data.pages < 0 || data.pages > 100 || !Number.isInteger(data.total) || data.total > 25000) throw new Error('search_index_invalid');
        return data;
      }
      const first = await batch(1), batches = [first];
      let page = 2;
      await Promise.all(Array.from({ length: Math.min(4, Math.max(0, first.pages - 1)) }, async () => {
        while (page <= first.pages) {
          const row = await batch(page++);
          if (row.pages !== first.pages || row.total !== first.total) throw new Error('search_index_changed');
          batches.push(row);
        }
      }));
      const unique = new Map();
      for (const row of batches) for (const entry of row.items) {
        if (!entry.key || !/^[a-z0-9-]+$/.test(entry.handle) || typeof entry.title !== 'string') throw new Error('search_index_invalid');
        unique.set(entry.key, { ...entry, kind });
      }
      if (kind === 'product' && unique.size !== first.total) throw new Error('search_index_incomplete');
      const result = { expires: Date.now() + 600000, items: [...unique.values()] };
      indexes.set(key, result);
      try { sessionStorage.setItem(key, JSON.stringify(result)); } catch (_) { /* Search works without storage. */ }
      return result.items;
    })();
    indexes.set(key, { pending });
    try { return await pending; } catch (error) { indexes.delete(key); throw error; }
  }
  function searchURL(url) {
    const target = new URL(url || '/search', location.origin);
    target.pathname = target.pathname.replace(/\/suggest\/?$/, '');
    target.search = ''; return target;
  }
  async function render(items, { url, signal, mode = 'compact' }) {
    if (!items.length) return [];
    const groups = [];
    for (let offset = 0; offset < items.length; offset += 20) groups.push(items.slice(offset, offset + 20));
    const rows = await Promise.all(groups.map(async batch => {
      const target = searchURL(url);
      target.search = new URLSearchParams({ q: mode + ':' + batch[0].kind + ':' + batch.map(item => item.handle).join('|'), view: 'ucw-search-render', type: 'product' });
      const doc = new DOMParser().parseFromString(await request(target, signal), 'text/html');
      const root = doc.querySelector('[data-ucw-search-response]');
      if (!root) throw new Error('search_invalid');
      const lookup = new Map(readItems(root).map(item => [item.key, item]));
      // Unpublished resources disappear; never replace them with a fuzzy match.
      return batch.flatMap(item => lookup.has(item.key) ? [{ ...item, html: lookup.get(item.key).html }] : []);
    }));
    abortCheck(signal); return rows.flat();
  }
  async function suggest(query, { url, signal, type = 'product,collection' } = {}) {
    const kinds = type === 'collection' ? ['printer'] : ['product', 'printer'];
    const sources = await Promise.all(kinds.map(index)); abortCheck(signal);
    const items = merge(sources, query);
    const products = items.filter(item => item.kind === 'product').slice(0, 6), printers = items.filter(item => item.kind === 'printer').slice(0, type === 'collection' ? 60 : 6);
    const rendered = await Promise.all([render(products, { url, signal }), render(printers, { url, signal })]);
    return { products: rendered[0], printers: rendered[1], totalPrinters: items.filter(item => item.kind === 'printer').length, partial: false };
  }
  async function products(query, { url, signal, next } = {}) {
    const items = merge([await index('product')], query); abortCheck(signal);
    const offset = next?.[0]?.offset || 0, batch = items.slice(offset, offset + 24);
    return { items: await render(batch, { url, signal, mode: 'cards' }), next: offset + 24 < items.length ? [{ offset: offset + 24 }] : [], partial: false, total: items.length };
  }
  window.UCWSearch = Object.freeze({ variants, compact, merge, score, suggest, products, readItems });
})();

